/**
 * API error message translation utilities.
 *
 * The backend FastAPI app surfaces 403 errors with `detail` strings that
 * sometimes include internal permission slugs (e.g. "Permission required:
 * employee.list"). These are useful for developers reading network logs
 * but are confusing implementation detail for end users.
 *
 * `formatApiErrorDetail()` normalises every FastAPI `detail` shape (string,
 * pydantic 422 array, dict) into a single readable line.
 *
 * `translateApiError()` adds friendly rewrites on top — used by the axios
 * interceptor so every UI surface gets readable copy "for free".
 */

type FastAPIErrorDetailItem = {
  type?: string;
  loc?: (string | number)[];
  msg?: string;
  message?: string;
  input?: unknown;
  ctx?: Record<string, unknown>;
};

type FastAPIErrorDetail =
  | string
  | FastAPIErrorDetailItem
  | FastAPIErrorDetailItem[]
  | undefined
  | null;

/**
 * Flatten any FastAPI `detail` shape into a single human-readable string.
 *
 * FastAPI returns three different `detail` shapes depending on the failure:
 *   - 4xx with `HTTPException(detail="...")`           → plain string
 *   - 422 from pydantic body validation                → array of error objs
 *   - Custom 4xx that returns a dict                   → object
 *
 * Without this normaliser, anything that does `(e as Error).message` ends up
 * with the useless axios string ("Request failed with status code 422")
 * because axios never reads `response.data` into the Error itself.
 */
export function formatApiErrorDetail(
  detail: FastAPIErrorDetail,
  fallback = 'Something went wrong',
): string {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail || fallback;

  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        const msg = item?.msg ?? item?.message ?? '';
        const field =
          Array.isArray(item?.loc) && item.loc.length
            ? String(item.loc[item.loc.length - 1])
            : '';
        if (field && msg) return `${field}: ${msg}`;
        return msg || field;
      })
      .filter(Boolean);
    return parts.join('; ') || fallback;
  }

  // Plain object — best-effort: prefer msg/message, then JSON-stringify.
  const obj = detail as FastAPIErrorDetailItem;
  if (obj.msg) return String(obj.msg);
  if (obj.message) return String(obj.message);
  try {
    return JSON.stringify(detail);
  } catch {
    return fallback;
  }
}

/**
 * Translate raw API error 'detail' fields into user-friendly toast copy.
 * Hides internal permission slugs and other implementation detail.
 *
 * Accepts both string `detail` (HTTPException) and structured `detail`
 * (pydantic 422 validation arrays) — gets flattened via
 * `formatApiErrorDetail` first.
 */
export function translateApiError(
  detail: FastAPIErrorDetail,
  fallback = 'Something went wrong',
): string {
  const flat = formatApiErrorDetail(detail, fallback);
  if (!flat || flat === fallback) return fallback;

  // Hide internal permission slugs — these are not actionable for end users.
  if (/^Permission required:/i.test(flat)) {
    return "You don't have permission to do that.";
  }
  if (/^One of the following permissions required:/i.test(flat)) {
    return "You don't have permission to do that.";
  }
  if (/^Superadmin access required/i.test(flat)) {
    return 'This action is restricted to admins.';
  }

  return flat;
}

/**
 * Extract a readable message from an arbitrary error caught in a UI handler.
 *
 * - Axios errors: read `error.response.data.detail` (the canonical FastAPI
 *   payload) and flatten it. Falls back to axios' own message if the body
 *   has nothing useful.
 * - Plain Error instances: return `.message`.
 * - Anything else: return the fallback.
 *
 * Use this in `catch (e) { setError(extractApiErrorMessage(e)) }` blocks so
 * a 422 from pydantic shows the actual field errors instead of the useless
 * "Request failed with status code 422".
 */
export function extractApiErrorMessage(
  err: unknown,
  fallback = 'Something went wrong',
): string {
  if (err == null) return fallback;

  // Axios errors carry `response.data` — preferred source.
  const maybeAxios = err as {
    isAxiosError?: boolean;
    response?: { data?: { detail?: FastAPIErrorDetail; message?: string } };
    message?: string;
  };
  if (maybeAxios?.isAxiosError && maybeAxios.response?.data) {
    const data = maybeAxios.response.data;
    if (data.detail != null) {
      return translateApiError(data.detail, fallback);
    }
    if (typeof data.message === 'string' && data.message) {
      return data.message;
    }
  }

  if (err instanceof Error && err.message) {
    // Don't surface raw axios "Request failed with status code N" — useless.
    if (/^Request failed with status code/i.test(err.message)) return fallback;
    return err.message;
  }

  if (typeof err === 'string') return err;
  return fallback;
}
